// Rotation feedback overlay drawn in the view plane while dragging a gizmo
// rotate ring: a translucent swept wedge + arc from the grab angle to the
// current (snapped) angle, a degree readout, and a dashed line from the gizmo
// center out to the cursor. All geometry is rebuilt each frame in WORLD space
// from a center + an in-plane orthonormal basis (R, U), so the caller just
// passes the view-plane frame it already computes. Parent it to an identity
// space (e.g. the floor scene root) so local == world.

import * as THREE from "three";
import { type SpriteLabel, makeLabel, setLabel } from "../floor/sprite-label";

const MAX_SEG = 96; // arc subdivisions cap

const _p = new THREE.Vector3();
const _q = new THREE.Vector3();
const _d1 = new THREE.Vector3();
const _d2 = new THREE.Vector3();
const _rq = new THREE.Quaternion();

export class RotationProtractor {
  readonly object3D = new THREE.Group();
  private sectorGeo = new THREE.BufferGeometry();
  private arcGeo = new THREE.BufferGeometry();
  private cursorGeo = new THREE.BufferGeometry();
  private sectorPos = new Float32Array(MAX_SEG * 3 * 3); // tris × 3 verts × xyz
  private arcPos = new Float32Array((MAX_SEG + 1) * 3);
  private cursorPos = new Float32Array(2 * 3);
  private sectorMat: THREE.MeshBasicMaterial;
  private arcMat: THREE.LineBasicMaterial;
  private cursorLine: THREE.Line;
  private label: SpriteLabel;

  constructor() {
    this.sectorMat = new THREE.MeshBasicMaterial({
      color: 0xffe04a,
      transparent: true,
      opacity: 0.18,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });
    this.arcMat = new THREE.LineBasicMaterial({
      color: 0xffe04a,
      transparent: true,
      depthTest: false,
    });

    this.sectorGeo.setAttribute("position", new THREE.BufferAttribute(this.sectorPos, 3));
    this.arcGeo.setAttribute("position", new THREE.BufferAttribute(this.arcPos, 3));
    this.cursorGeo.setAttribute("position", new THREE.BufferAttribute(this.cursorPos, 3));

    const sector = new THREE.Mesh(this.sectorGeo, this.sectorMat);
    sector.renderOrder = 990;
    const arc = new THREE.Line(this.arcGeo, this.arcMat);
    arc.renderOrder = 991;
    this.cursorLine = new THREE.Line(
      this.cursorGeo,
      new THREE.LineDashedMaterial({
        color: 0xffffff,
        transparent: true,
        depthTest: false,
        dashSize: 0.05,
        gapSize: 0.035,
      }),
    );
    this.cursorLine.renderOrder = 991;
    this.label = makeLabel(0.5, 0.155, 992);

    this.object3D.add(sector, arc, this.cursorLine, this.label.sprite);
    this.object3D.visible = false;
  }

  hide(): void {
    this.object3D.visible = false;
  }

  // Drawn in the ROTATION RING's plane (perpendicular to `axis`), so it's
  // coplanar with the ring — not facing the camera. `refDir` is the grab
  // reference direction (unit, in that plane); the wedge sweeps it about
  // `axis` by `sweep` radians. radius: arc radius (world). cursor: cursor's
  // world point in the ring plane (dashed-line end). degrees: readout value.
  update(
    center: THREE.Vector3,
    axis: THREE.Vector3,
    refDir: THREE.Vector3,
    sweep: number,
    radius: number,
    cursor: THREE.Vector3,
    degrees: number,
    color: number,
  ): void {
    this.object3D.visible = true;
    const n = Math.max(
      2,
      Math.min(MAX_SEG, Math.ceil(Math.abs(sweep) / (Math.PI / 48)) + 1),
    );
    // Direction at sweep-fraction t: refDir rotated about the axis.
    const dirAt = (t: number, out: THREE.Vector3): THREE.Vector3 => {
      _rq.setFromAxisAngle(axis, t);
      return out.copy(refDir).applyQuaternion(_rq);
    };

    // Arc line strip + filled wedge (triangle per arc segment, fanned from
    // the center), all in the ring plane.
    let si = 0;
    for (let i = 0; i < n; i++) {
      const t = (sweep * i) / (n - 1);
      dirAt(t, _d1);
      _p.copy(center).addScaledVector(_d1, radius);
      this.arcPos[i * 3] = _p.x;
      this.arcPos[i * 3 + 1] = _p.y;
      this.arcPos[i * 3 + 2] = _p.z;
      if (i < n - 1) {
        const t2 = (sweep * (i + 1)) / (n - 1);
        dirAt(t2, _d2);
        _q.copy(center).addScaledVector(_d2, radius);
        this.sectorPos[si++] = center.x;
        this.sectorPos[si++] = center.y;
        this.sectorPos[si++] = center.z;
        this.sectorPos[si++] = _p.x;
        this.sectorPos[si++] = _p.y;
        this.sectorPos[si++] = _p.z;
        this.sectorPos[si++] = _q.x;
        this.sectorPos[si++] = _q.y;
        this.sectorPos[si++] = _q.z;
      }
    }
    this.arcGeo.setDrawRange(0, n);
    this.arcGeo.attributes.position.needsUpdate = true;
    this.sectorGeo.setDrawRange(0, (n - 1) * 3);
    this.sectorGeo.attributes.position.needsUpdate = true;

    // Dashed line from center to the cursor (also in the ring plane).
    this.cursorPos.set([center.x, center.y, center.z, cursor.x, cursor.y, cursor.z]);
    this.cursorGeo.attributes.position.needsUpdate = true;
    this.cursorLine.computeLineDistances();

    this.sectorMat.color.setHex(color);
    this.arcMat.color.setHex(color);

    // Readout pill pinned at the gizmo center so it stays put instead of
    // sliding around the circle as the sweep changes. Set the text FIRST so the
    // pill's measured aspect is current, then size width = height × aspect every
    // frame — otherwise the pill keeps a fixed aspect and stretches the text as
    // the digit count changes (and only self-corrects on the frames it changes).
    setLabel(this.label, `${Math.round(degrees)}°`, "#ffffff");
    const h = radius * 1.4 * 0.31; // on-screen pill height
    this.label.sprite.position.copy(center);
    this.label.sprite.scale.set(h * this.label.aspect, h, 1);
    this.label.sprite.visible = true; // makeLabel starts hidden (pool default)
  }
}
